import { useId } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the Checkbox component.
 *
 * `className` is intentionally omitted — Checkbox is a sealed design system
 * component. All visual state is controlled through named props.
 */
export interface CheckboxProps {
  /** Current checked state. */
  checked: boolean;
  /** Called with the new checked value when the user interacts. */
  onChange: (checked: boolean) => void;
  /** Label text rendered next to the checkbox. Always required for accessibility. */
  label: string;
  /** Disables interaction and applies reduced opacity. @default false */
  disabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer Checkbox component.
 *
 * Uses the `check-box` / `check-box-selected` icons from the icon sprite.
 * The native `<input type="checkbox">` is visually hidden but remains in the
 * DOM for full keyboard and screen reader accessibility.
 *
 * @example
 * <Checkbox
 *   checked={agreed}
 *   onChange={setAgreed}
 *   label="Don't show this again"
 * />
 */
export function Checkbox({ checked, onChange, label, disabled = false }: CheckboxProps) {
  const id = useId();

  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      )}
    >
      {/* Visually hidden native checkbox — keeps keyboard + screen reader support */}
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />

      {/* Icon-based visual checkbox */}
      <span aria-hidden="true" className="flex-shrink-0">
        <Icon
          name={checked ? 'check-box-selected' : 'check-box'}
          size="large"
          color={disabled ? 'disabled' : 'interactive'}
        />
      </span>

      <Typography variant="body" size="medium">
        {label}
      </Typography>
    </label>
  );
}

export default Checkbox;
